import React from 'react';
import {
  ApplianceShell,
  HorizontalSplit,
  VerticalSplit,
  VerticalHandle,
  HorizontalHandle,
  ToeKick,
  TOE_KICK_HEIGHT_M,
} from './ApplianceFrame';

const INCHES_TO_METERS = 0.0254;

/**
 * Refrigerator variants:
 *   - top-freezer:    narrow freezer band on top (~32%), full-width
 *                     fridge door below. Vertical handles on the
 *                     right edge of each door.
 *   - bottom-freezer: full-width fridge door on top (~68%), full-
 *                     width freezer drawer on the bottom. Horizontal
 *                     drawer pull on the freezer. Default.
 *   - side-by-side:   two full-height compartments split by width
 *                     ratio (~40% freezer left / 60% fridge right),
 *                     each with a vertical handle facing the divider.
 *                     This is the natural layout for >= 42" units.
 *   - french-door:    fridge zone on top (~68%) split into two
 *                     mirrored doors with handles facing the center,
 *                     plus a full-width freezer drawer below.
 *   - counter-depth:  same face layout as french-door (the field
 *                     describes depth, not face geometry, but it
 *                     gets its own dispatch case for completeness);
 *                     rendered with a flush thin top trim line that
 *                     hints at the flush-to-cabinets profile.
 *
 * Width-based default: when no variant field is set and width is at
 * or above the side-by-side threshold (42"), default to side-by-side
 * — matches `refrigeratorRenderer` in applianceRenderers.js. Below
 * that, default to bottom-freezer (the modern residential default).
 *
 * Field: `obj.subtype` — same canonical field used by Range,
 * WallOven, Dishwasher. The kitchen pipeline does not currently
 * emit a refrigerator subtype (see TYPE_PROFILES.fridge in
 * materializeCandidate.js — only `type: 'refrigerator'`), so callers
 * should pass it explicitly until that field lands.
 */

const SIDE_BY_SIDE_WIDTH_THRESHOLD_M = 42 * INCHES_TO_METERS;
const FREEZER_TOP_RATIO = 0.32;
const FREEZER_BOTTOM_RATIO = 0.32;
const SIDE_BY_SIDE_FREEZER_WIDTH_RATIO = 0.4;

const VALID_VARIANTS = new Set([
  'top-freezer',
  'bottom-freezer',
  'side-by-side',
  'french-door',
  'counter-depth',
]);

export function resolveRefrigeratorVariant(obj = {}, widthMeters = 0) {
  const subtype = obj.subtype;
  if (VALID_VARIANTS.has(subtype)) return subtype;
  if (widthMeters >= SIDE_BY_SIDE_WIDTH_THRESHOLD_M) return 'side-by-side';
  // Default narrow fridges to top-freezer so the schematic matches the
  // 3D `refrigeratorRenderer`, which renders top-mount for widths below
  // the 42" side-by-side threshold (it never emits a bottom-freezer
  // layout). Keep `bottom-freezer` available as an explicit subtype.
  return 'top-freezer';
}

export function RefrigeratorSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  if (view === 'left' || view === 'right') {
    return (
      <RefrigeratorSideView
        view={view}
        depthMeters={depthMeters}
        heightMeters={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const variant = resolveRefrigeratorVariant(obj, width);
  const body = renderBody(variant, {
    width,
    height,
    strokeColor,
    strokeWidth,
  });
  return (
    <ApplianceShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {body}
      <ToeKick
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </ApplianceShell>
  );
}

RefrigeratorSchematic.supportsSideViews = true;

/**
 * Side elevation of a refrigerator: tall rectangle (depth × height)
 * with a slim front-edge bezel (vertical line just inside the front
 * face) and a vertical handle line running near the front edge to
 * suggest a full-height door pull. Toe-kick recess at the bottom.
 *
 * Convention: LEFT view places the front face at x = depthMeters
 * (right edge of the drawing). Mirrored for RIGHT.
 */
function RefrigeratorSideView({
  depthMeters,
  heightMeters,
  strokeColor,
  strokeWidth,
  view,
}) {
  const w = depthMeters;
  const h = heightMeters;
  const toeY = h - TOE_KICK_HEIGHT_M;
  // Slim bezel just inside the front face (about 1.5" / 0.04m).
  const bezelInset = Math.min(w * 0.06, 0.04);
  // Handle line at the front edge — vertical bar running most of
  // the door height. Sits between the bezel and the front face.
  const handleX = w - bezelInset * 0.5;
  const handleProtrusion = Math.min(w * 0.03, 0.015);
  const handleTop = h * 0.1;
  const handleBottom = toeY - h * 0.05;

  const sideChildren = (
    <>
      {/* Toe-kick recess line */}
      {h > TOE_KICK_HEIGHT_M && (
        <line
          x1={0}
          y1={toeY}
          x2={w}
          y2={toeY}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {/* Slim front bezel line */}
      <line
        x1={w - bezelInset}
        y1={0}
        x2={w - bezelInset}
        y2={toeY}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Vertical handle bar (full-height handle on the front edge,
          slightly projecting outward). */}
      <line
        x1={handleX + handleProtrusion}
        y1={handleTop}
        x2={handleX + handleProtrusion}
        y2={handleBottom}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Handle standoffs at top and bottom connecting to the door
          face. */}
      <line
        x1={handleX}
        y1={handleTop}
        x2={handleX + handleProtrusion}
        y2={handleTop}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={handleX}
        y1={handleBottom}
        x2={handleX + handleProtrusion}
        y2={handleBottom}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </>
  );

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {view === 'right' ? (
        <g transform={`translate(${w}, 0) scale(-1, 1)`}>{sideChildren}</g>
      ) : (
        sideChildren
      )}
    </svg>
  );
}

function renderBody(variant, props) {
  if (variant === 'top-freezer') return <TopFreezerBody {...props} />;
  if (variant === 'side-by-side') return <SideBySideBody {...props} />;
  if (variant === 'french-door') return <FrenchDoorBody {...props} />;
  if (variant === 'counter-depth') return <CounterDepthBody {...props} />;
  return <BottomFreezerBody {...props} />;
}

function bodyBottomY(height) {
  return Math.max(0, height - TOE_KICK_HEIGHT_M);
}

function TopFreezerBody({ width, height, strokeColor, strokeWidth }) {
  const bottom = bodyBottomY(height);
  const splitY = bottom * FREEZER_TOP_RATIO;
  const handleX = width - Math.min(width * 0.08, 0.06);
  // Matches `buildTopMountFridge` in applianceRenderers.js: the freezer
  // handle is centered on the freezer door; the fridge handle's TOP sits
  // ~15% of the freezer height BELOW the separator (not centered on the
  // fridge door). The visible read is "matched pair of bars flanking the
  // separator" rather than "centered on each door".
  const freezerH = splitY;
  const fridgeH = bottom - splitY;
  const freezerHandleLen = freezerH * 0.7;
  const fridgeHandleLen = fridgeH * 0.7;
  const fridgeHandleClearance = freezerH * 0.15;
  const fridgeHandleTopY = splitY + fridgeHandleClearance;
  const fridgeHandleCy = fridgeHandleTopY + fridgeHandleLen / 2;
  return (
    <g>
      <HorizontalSplit
        width={width}
        y={splitY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalHandle
        x={handleX}
        cy={freezerH / 2}
        length={freezerHandleLen}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalHandle
        x={handleX}
        cy={fridgeHandleCy}
        length={fridgeHandleLen}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function BottomFreezerBody({ width, height, strokeColor, strokeWidth }) {
  const bottom = bodyBottomY(height);
  const splitY = bottom * (1 - FREEZER_BOTTOM_RATIO);
  const handleX = width - Math.min(width * 0.08, 0.06);
  return (
    <g>
      <HorizontalSplit
        width={width}
        y={splitY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalHandle
        x={handleX}
        cy={splitY / 2}
        length={splitY * 0.7}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <HorizontalHandle
        cx={width / 2}
        y={splitY + (bottom - splitY) * 0.2}
        length={width * 0.55}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function SideBySideBody({ width, height, strokeColor, strokeWidth }) {
  const bottom = bodyBottomY(height);
  const splitX = width * SIDE_BY_SIDE_FREEZER_WIDTH_RATIO;
  const handleInset = Math.min(width * 0.04, 0.06);
  const cy = bottom / 2;
  const handleLen = bottom * 0.7;
  return (
    <g>
      <VerticalSplit
        x={splitX}
        height={bottom}
        y1={0}
        y2={bottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalHandle
        x={splitX - handleInset}
        cy={cy}
        length={handleLen}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalHandle
        x={splitX + handleInset}
        cy={cy}
        length={handleLen}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function FrenchDoorBody({ width, height, strokeColor, strokeWidth }) {
  const bottom = bodyBottomY(height);
  const splitY = bottom * (1 - FREEZER_BOTTOM_RATIO);
  const midX = width / 2;
  const handleInset = Math.min(width * 0.04, 0.06);
  const fridgeHandleCy = splitY / 2;
  const fridgeHandleLen = splitY * 0.7;
  return (
    <g>
      <HorizontalSplit
        width={width}
        y={splitY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalSplit
        x={midX}
        height={splitY}
        y1={0}
        y2={splitY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalHandle
        x={midX - handleInset}
        cy={fridgeHandleCy}
        length={fridgeHandleLen}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <VerticalHandle
        x={midX + handleInset}
        cy={fridgeHandleCy}
        length={fridgeHandleLen}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <HorizontalHandle
        cx={midX}
        y={splitY + (bottom - splitY) * 0.2}
        length={width * 0.55}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Counter-depth: same face layout as french-door (most counter-depth
 * units in market are french-door form factor) with an added thin
 * top trim line (~1" below the top edge) hinting at the flush-with-
 * cabinets profile. Field-driven; depth itself is not visible in an
 * elevation, so the cue is decorative.
 */
function CounterDepthBody({ width, height, strokeColor, strokeWidth }) {
  const trimY = Math.min(0.025, height * 0.02);
  return (
    <g>
      <HorizontalSplit
        width={width}
        y={trimY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <FrenchDoorBody
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}
